import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { ArrowLeft, Pause, Play, Square, X } from 'lucide-react';
import { useI18n } from '../i18n';
import { useApp } from '../state';
import { currentVersion, type HabitRec } from '../domain/model';
import { dayTotal, logAmount, setTaskDone, toggleBinary } from '../domain/actions';
import {
  FOCUS_MAX_MINUTES,
  FOCUS_MIN_MINUTES,
  FOCUS_PRESETS,
  deleteFocusRecord,
  finishFocus,
  isComplete,
  loadFocus,
  pauseFocus,
  remainingMs,
  resumeFocus,
  saveFocus,
  startFocus,
  type FocusState,
} from '../domain/focus';
import { Field } from '../ui/Field';
import { ProgressRing } from '../ui/ProgressRing';
import { Sheet } from '../ui/Sheet';
import { useToast } from '../ui/Toast';

function fmtClock(ms: number): string {
  const total = Math.ceil(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/** Temporizador de enfoque: preparar, en curso y terminado. */
export function FocusScreen() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [state, setState] = useState<FocusState | null | undefined>(undefined);
  const [now, setNow] = useState(() => Date.now());
  const [finishing, setFinishing] = useState(false);

  useEffect(() => {
    let alive = true;
    void loadFocus().then((s) => alive && setState(s));
    // Solo refresca la pantalla: el tiempo se calcula siempre a partir de instantes.
    const tick = () => setNow(Date.now());
    const id = window.setInterval(tick, 1000);
    document.addEventListener('visibilitychange', tick);
    return () => {
      alive = false;
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', tick);
    };
  }, []);

  const update = (s: FocusState | null) => {
    setState(s);
    void saveFocus(s);
  };

  const running = state && state.runningSince !== null && !isComplete(state, now);
  useEffect(() => {
    if (!state) return;
    const base = document.title;
    document.title = running ? `${fmtClock(remainingMs(state, now))} · ${t('focus.title')}` : base;
    return () => {
      document.title = base;
    };
  }, [state, now, running, t]);

  return (
    <div className="stack-l">
      <header className="page-header" style={{ marginBottom: 0 }}>
        <button type="button" className="icon-btn" aria-label={t('common.back')} onClick={() => navigate(-1)}>
          <ArrowLeft size={22} aria-hidden="true" />
        </button>
        <h1 style={{ flex: 1 }}>{t('focus.title')}</h1>
      </header>
      {state === undefined ? null : state === null ? (
        <FocusSetup onStart={(s) => update(s)} />
      ) : finishing || isComplete(state, now) ? (
        <FocusFinish state={state} now={now} early={!isComplete(state, now)} onDone={() => {
            setFinishing(false);
            setState(null);
          }} onBack={() => setFinishing(false)} />
      ) : (
        <FocusRunning state={state} now={now} onChange={update} onFinish={() => setFinishing(true)} />
      )}
      <p className="muted xsmall">{t('focus.limitation')}</p>
    </div>
  );
}

function FocusSetup({ onStart }: { onStart: (s: FocusState) => void }) {
  const { t } = useI18n();
  const { data } = useApp();
  const preset = new URLSearchParams(window.location.search).get('habit');
  const [minutes, setMinutes] = useState(25);
  const [custom, setCustom] = useState('');
  const [habitId, setHabitId] = useState<string>(preset && data.habits.some((h) => h.id === preset) ? preset : '');
  const [label, setLabel] = useState('');
  const [error, setError] = useState<string | null>(null);
  const options = data.habits.filter((h) => !h.deletedAt && !h.archivedAt && !(h.kind === 'task' && h.completedAt));

  return (
    <form
      className="card stack"
      // Validación propia: el mensaje queda asociado al campo y se anuncia (role="alert").
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        const m = custom ? Number(custom) : minutes;
        if (!Number.isInteger(m) || m < FOCUS_MIN_MINUTES || m > FOCUS_MAX_MINUTES) {
          setError(t('focus.durationError', { min: FOCUS_MIN_MINUTES, max: FOCUS_MAX_MINUTES }));
          return;
        }
        onStart(startFocus(m, habitId || null, label || null, Date.now()));
      }}
    >
      <fieldset style={{ border: 0, padding: 0, margin: 0 }} className="stack">
        <legend className="field__label">{t('focus.duration')}</legend>
        <div className="row" style={{ flexWrap: 'wrap' }}>
          {FOCUS_PRESETS.map((p) => (
            <button
              key={p}
              type="button"
              className="chip-toggle"
              aria-pressed={!custom && minutes === p}
              onClick={() => {
                setMinutes(p);
                setCustom('');
                setError(null);
              }}
            >
              {t('focus.minutes', { count: p })}
            </button>
          ))}
        </div>
      </fieldset>
      <Field label={t('focus.custom')} help={t('focus.customHelp', { min: FOCUS_MIN_MINUTES, max: FOCUS_MAX_MINUTES })} error={error}>
        {(p) => (
          <input
            {...p}
            className="input num"
            inputMode="numeric"
            type="number"
            min={FOCUS_MIN_MINUTES}
            max={FOCUS_MAX_MINUTES}
            value={custom}
            onChange={(e) => {
              setCustom(e.target.value);
              setError(null);
            }}
          />
        )}
      </Field>
      <Field label={`${t('focus.linkTo')} (${t('common.optional')})`}>
        {(p) => (
          <select {...p} className="select" value={habitId} onChange={(e) => setHabitId(e.target.value)}>
            <option value="">{t('focus.noLink')}</option>
            {options.map((h) => (
              <option key={h.id} value={h.id}>
                {h.name}
              </option>
            ))}
          </select>
        )}
      </Field>
      <Field label={`${t('focus.label')} (${t('common.optional')})`}>{(p) => <input {...p} className="input" maxLength={80} value={label} onChange={(e) => setLabel(e.target.value)} />}</Field>
      <button type="submit" className="btn btn--primary btn--block">
        <Play size={18} aria-hidden="true" /> {t('focus.start')}
      </button>
    </form>
  );
}

function FocusRunning({ state, now, onChange, onFinish }: { state: FocusState; now: number; onChange: (s: FocusState | null) => void; onFinish: () => void }) {
  const { t } = useI18n();
  const { data } = useApp();
  const [confirmCancel, setConfirmCancel] = useState(false);
  const remaining = remainingMs(state, now);
  const paused = state.runningSince === null;
  const habit = state.habitId ? data.habits.find((h) => h.id === state.habitId) : undefined;
  return (
    <section className="card stack" style={{ alignItems: 'center', textAlign: 'center' }} aria-label={t('focus.session')}>
      <ProgressRing value={1 - remaining / (state.plannedSeconds * 1000)} label={t('focus.remaining', { time: fmtClock(remaining) })} />
      <div className="timer num" role="timer" aria-live="off" aria-label={t('focus.remaining', { time: fmtClock(remaining) })}>
        {fmtClock(remaining)}
      </div>
      <div className="muted small">
        {paused ? t('focus.paused') : t('focus.running')}
        {(habit || state.label) && ` · ${habit?.name ?? state.label}`}
      </div>
      <div className="row" style={{ flexWrap: 'wrap', justifyContent: 'center' }}>
        {paused ? (
          <button type="button" className="btn btn--primary" onClick={() => onChange(resumeFocus(state, Date.now()))}>
            <Play size={18} aria-hidden="true" /> {t('focus.resume')}
          </button>
        ) : (
          <button type="button" className="btn btn--primary" onClick={() => onChange(pauseFocus(state, Date.now()))}>
            <Pause size={18} aria-hidden="true" /> {t('focus.pause')}
          </button>
        )}
        <button type="button" className="btn" onClick={onFinish}>
          <Square size={18} aria-hidden="true" /> {t('focus.finish')}
        </button>
        <button type="button" className="btn btn--ghost" onClick={() => setConfirmCancel(true)}>
          <X size={18} aria-hidden="true" /> {t('focus.cancel')}
        </button>
      </div>
      <Sheet open={confirmCancel} onClose={() => setConfirmCancel(false)} title={t('focus.cancelTitle')} description={t('focus.cancelBody')}>
        <button type="button" className="btn btn--danger btn--block" onClick={() => onChange(null)}>
          {t('focus.cancelConfirm')}
        </button>
        <button type="button" className="btn btn--ghost btn--block" onClick={() => setConfirmCancel(false)}>
          {t('focus.keepGoing')}
        </button>
      </Sheet>
    </section>
  );
}

/** Qué se puede registrar en el hábito vinculado al terminar. */
function linkedAction(h: HabitRec | undefined): 'minutes' | 'hours' | 'mark' | 'task' | null {
  if (!h || h.deletedAt) return null;
  if (h.kind === 'task') return h.completedAt ? null : 'task';
  if (h.kind !== 'build') return null;
  const v = currentVersion(h);
  if (v.goalType === 'numeric' && v.unit === 'min') return 'minutes';
  if (v.goalType === 'numeric' && v.unit === 'h') return 'hours';
  return v.goalType === 'binary' ? 'mark' : null;
}

function FocusFinish({ state, now, early, onDone, onBack }: { state: FocusState; now: number; early: boolean; onDone: () => void; onBack: () => void }) {
  const { t } = useI18n();
  const { data, ctx } = useApp();
  const toast = useToast();
  const habit = state.habitId ? data.habits.find((h) => h.id === state.habitId) : undefined;
  const action = linkedAction(habit);
  const [apply, setApply] = useState(action === 'minutes' || action === 'hours');
  const [busy, setBusy] = useState(false);
  const activeMin = Math.round(Math.min(state.plannedSeconds * 1000, state.accumulatedMs + (state.runningSince ? now - state.runningSince : 0)) / 60_000);

  const save = async (at: number) => {
    setBusy(true);
    try {
      const { id, activeSeconds } = await finishFocus(state, at, ctx.today);
      const mins = Math.round(activeSeconds / 60);
      if (habit && apply && action) {
        if (action === 'minutes' && mins > 0) await logAmount(habit, ctx.today, mins, ctx, data.entries, data.overrides);
        if (action === 'hours' && mins > 0) await logAmount(habit, ctx.today, Math.round((mins / 60) * 100) / 100, ctx, data.entries, data.overrides);
        if (action === 'mark' && dayTotal(data.entries, habit.id, ctx.today) === 0) await toggleBinary(habit, ctx.today, ctx, data.entries, data.overrides);
        if (action === 'task') await setTaskDone(habit, true);
      }
      onDone();
      toast.show(t('focus.saved', { count: mins }), { label: t('common.undo'), run: () => void deleteFocusRecord(id) });
    } catch {
      toast.show(t('errors.save'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card stack" aria-label={t('focus.doneTitle')}>
      <h2>{early ? t('focus.finishEarlyTitle') : t('focus.doneTitle')}</h2>
      <p>{t('focus.doneBody', { count: activeMin })}</p>
      {habit && action && (
        <label className="row">
          <input type="checkbox" checked={apply} onChange={(e) => setApply(e.target.checked)} />
          {action === 'minutes' || action === 'hours'
            ? t('focus.logMinutes', { count: activeMin, name: habit.name })
            : action === 'mark'
              ? t('focus.markDone', { name: habit.name })
              : t('focus.completeTask', { name: habit.name })}
        </label>
      )}
      <button type="button" className="btn btn--primary btn--block" disabled={busy} onClick={() => void save(Date.now())}>
        {t('focus.save')}
      </button>
      {early && (
        <button type="button" className="btn btn--ghost btn--block" onClick={onBack}>
          {t('focus.keepGoing')}
        </button>
      )}
    </section>
  );
}
